import {
  IFacturaEDialogModel
} from '@/components/billing/FacturaEDialog/types/FacturaEDialogTypes'
import { computed, ComputedRef } from 'vue'
import { b64toBlob, createLink } from '@/helpers/blob'
import { FacturaEService } from '@/services/FacturaEService'
import useDialog from '@/composables/useDialog'
import { useI18n } from 'vue-i18n-composable'

interface IActionsFacturaEDialog {
  hasDocumentGenerated: ComputedRef<boolean>
  hasValidatorErrors: ComputedRef<boolean>
  generateDocument: () => Promise<void>
  resetDocument: () => void
  downloadDocument: () => void
  next: () => void
  prev: () => void
  validate: () => Promise<void>
  send: () => Promise<void>
  close: () => void
}

export default function useActionsFacturaEDialog(model: IFacturaEDialogModel): IActionsFacturaEDialog {
  const { getDialogSuccess, getDialogError } = useDialog()
  const { t } = useI18n()
  const hasDocumentGenerated = computed((): boolean => 0 !== model.documentId)
  const hasValidatorErrors = computed((): boolean => Boolean(model.validatorErrorList.length))
  const generateDocument = async () => {
    const { data } = await new FacturaEService().generateDocument({
      invoiceId: model.invoiceId,
      templateName: String(model?.selectedTemplate?.templateName),
      templateId: String(model?.selectedTemplate?.idEntity),
      fileName: String(model?.selectedTemplate?.name),
    })
    if (data.error) {
      getDialogError({ message: data.error })
      close()
      return
    }
    if (data.validatorErrors) {
      model.validatorErrorList = data.validatorErrors
      return
    }
    model.documentId = data ? data.documentId : undefined
    model.documentContent = data ? data.documentContent : undefined
  }
  const resetDocument = () => {
    model.documentId = 0
    model.documentContent = null
  }
  const downloadDocument = () => createLink(
    URL.createObjectURL(
      b64toBlob(String(model.documentContent), 'application/pdf')
    ),
    '',
    true
  )
  const next = () => model.step++
  const prev = () => model.step--
  const close = () => model.step = -1
  const validate = async () => {
    const { data } = await new FacturaEService().validate(model.invoiceId)
    if (data.error) {
      getDialogError({ message: data.error })
      close()
      return
    }
    if (data.validatorErrors) {
      model.validatorErrorList = data.validatorErrors
      return
    }
  }
  const send = async () => {
    const { data } = await new FacturaEService().send(model.invoiceId)
    if (data.error) {
      getDialogError({ message: data.error })
      close()
      return
    }
    if (data.validatorErrors) {
      model.validatorErrorList = data.validatorErrors
      return
    }
    getDialogSuccess({
      message: t(
        'components.dialog.facturae_success',
        { description: model.invoiceDescription }
      ).toString()
    })
    close()
  }

  return {
    hasDocumentGenerated,
    hasValidatorErrors,
    generateDocument,
    downloadDocument,
    resetDocument,
    next,
    prev,
    validate,
    send,
    close
  }
}
